import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTachometerAlt,
  faUserTie,
  faNewspaper,
  faBullhorn,
  faGavel,
  faUsers,
  faInfoCircle,
  faEnvelope,
  faNavicon,
  faImages,
  faBuilding,
  faBriefcase,
  faSitemap,
  faComments,
  faProjectDiagram,
  faChevronDown,
  faChevronUp,
  faHome,
  faBook,
  faPhotoVideo,
  faTasks,
  faServer,
} from "@fortawesome/free-solid-svg-icons";

import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";

interface Group {
  id: string;
  labelAr: string;
  labelFr: string;
  icon: any;
  links: Array<{
    to: string;
    labelAr: string;
    labelFr: string;
    icon: any;
  }>;
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const { logout } = useAuth();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(["content", "ministry"])
  );

  const toggleGroup = (groupId: string) => {
    const newExpandedGroups = new Set(expandedGroups);
    if (newExpandedGroups.has(groupId)) {
      newExpandedGroups.delete(groupId);
    } else {
      newExpandedGroups.add(groupId);
    }
    setExpandedGroups(newExpandedGroups);
  };

  const groups: Group[] = [
    {
      id: "backToPublic",
      labelAr: "العودة للموقع",
      labelFr: "Retour au Site",
      icon: faHome,
      links: [
        {
          to: "/",
          labelAr: "العودة للموقع",
          labelFr: "Retour au Site",
          icon: faHome,
        },
      ],
    },
    {
      id: "dashboard",
      labelAr: "لوحة التحكم",
      labelFr: "Tableau de Bord",
      icon: faTachometerAlt,
      links: [
        {
          to: "/admin/dashboard",
          labelAr: "الإحصائيات",
          labelFr: "Statistiques",
          icon: faTachometerAlt,
        },
      ],
    },
    {
      id: "ministry",
      labelAr: "الوزارة",
      labelFr: "Le Ministère",
      icon: faHome,
      links: [
        {
          to: "/admin/ministers",
          labelAr: "الوزراء",
          labelFr: "Ministres",
          icon: faUserTie,
        },
        {
          to: "/admin/cabinetministre",
          labelAr: "مكتب الوزير",
          labelFr: "Cabinet du Ministre",
          icon: faBriefcase,
        },
        {
          to: "/admin/mot-ministre",
          labelAr: "كلمة الوزير",
          labelFr: "Mot du Ministre",
          icon: faComments,
        },
        {
          to: "/admin/sousetablissements",
          labelAr: "المؤسسات الفرعية",
          labelFr: "Établissements Sous-tutelle",
          icon: faBuilding,
        },
        {
          to: "/admin/structure-admin",
          labelAr: "الهياكل الإدارية",
          labelFr: "Structures Administratives",
          icon: faSitemap,
        },
      ],
    },
    {
      id: "projects",
      labelAr: "المشاريع",
      labelFr: "Projets",
      icon: faProjectDiagram,
      links: [
        {
          to: "/admin/projets",
          labelAr: "المشاريع",
          labelFr: "Projets",
          icon: faProjectDiagram,
        },
      ],
    },
    {
      id: "procedures",
      labelAr: "الإجراءات والخدمات",
      labelFr: "Procédures et Services",
      icon: faTasks,
      links: [
        {
          to: "/admin/procedures",
          labelAr: "الإجراءات والخدمات",
          labelFr: "Procédures et Services",
          icon: faTasks,
        },
      ],
    },
    {
      id: "content",
      labelAr: "المحتوى",
      labelFr: "Contenu",
      icon: faNewspaper,
      links: [
        {
          to: "/admin/news",
          labelAr: "الأخبار",
          labelFr: "Actualités",
          icon: faNewspaper,
        },
        {
          to: "/admin/news/categories",
          labelAr: "تصنيفات الأخبار",
          labelFr: "Catégories d'Actualités",
          icon: faNavicon,
        },
        {
          to: "/admin/announcements",
          labelAr: "الإعلانات",
          labelFr: "Annonces",
          icon: faBullhorn,
        },
        {
          to: "/admin/legislations",
          labelAr: "التشريعات",
          labelFr: "Législations",
          icon: faGavel,
        },
      ],
    },
    {
      id: "media",
      labelAr: "المكتبة المتعددة الوسائط",
      labelFr: "Médiathèque",
      icon: faPhotoVideo,
      links: [
        {
          to: "/admin/bibliotheque",
          labelAr: "المكتبة",
          labelFr: "Bibliothèque",
          icon: faPhotoVideo,
        },
        {
          to: "/admin/bibliotheque/categories",
          labelAr: "تصنيفات المكتبة",
          labelFr: "Catégories de la Bibliothèque",
          icon: faBook,
        },
      ],
    },
    {
      id: "presentation",
      labelAr: "العرض",
      labelFr: "Présentation",
      icon: faImages,
      links: [
        {
          to: "/admin/sliders",
          labelAr: "السلايدر",
          labelFr: "Slider",
          icon: faImages,
        },
        {
          to: "/admin/information",
          labelAr: "المعلومات العامة",
          labelFr: "Informations Générales",
          icon: faInfoCircle,
        },
      ],
    },
    {
      id: "administration",
      labelAr: "الإدارة",
      labelFr: "Administration",
      icon: faUsers,
      links: [
        {
          to: "/admin/users",
          labelAr: "المستخدمين",
          labelFr: "Utilisateurs",
          icon: faUsers,
        },
        {
          to: "/admin/messages",
          labelAr: "الرسائل",
          labelFr: "Messages",
          icon: faEnvelope,
        },
      ],
    },
  ];

  return (
    <div className="w-64 min-h-screen bg-white shadow-md flex flex-col">
      <div className="p-6 text-2xl font-bold text-mauritania-green-dark border-b">
        لوحة الإدارة
      </div>
      <nav className="p-4">
        <ul className="space-y-2">
          {groups.map((group) => {
            const isExpanded = expandedGroups.has(group.id);
            const hasActiveLink = group.links.some(
              (link) => location.pathname === link.to
            );

            return (
              <li key={group.id} className="space-y-1">
                {group.id === "dashboard" || group.id === "backToPublic" ? (
                  // Dashboard and Back to Public are not collapsible
                  <Link
                    to={group.links[0].to}
                    className={`flex items-center px-4 py-2 rounded-md hover:bg-mauritania-green/10 ${
                      location.pathname === group.links[0].to
                        ? "bg-mauritania-green/20 text-mauritania-green-dark font-semibold"
                        : "text-gray-700"
                    } ${group.id === "backToPublic" ? "border-b pb-4 mb-4" : ""}`}
                  >
                    <FontAwesomeIcon icon={group.icon} className="ml-2" />
                    <span>
                      {language === "ar" ? group.labelAr : group.labelFr}
                    </span>
                  </Link>
                ) : (
                  // Collapsible groups
                  <div className="space-y-1">
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className={`flex items-center justify-between w-full px-4 py-2 rounded-md hover:bg-mauritania-green/10 ${
                        hasActiveLink
                          ? "bg-mauritania-green/20 text-mauritania-green-dark font-semibold"
                          : "text-gray-700"
                      }`}
                    >
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={group.icon} className="ml-2" />
                        <span>
                          {language === "ar" ? group.labelAr : group.labelFr}
                        </span>
                      </div>
                      <FontAwesomeIcon
                        icon={isExpanded ? faChevronUp : faChevronDown}
                        className="text-sm"
                      />
                    </button>

                    {isExpanded && (
                      <ul className="mr-8 space-y-1">
                        {group.links.map((link) => (
                          <li key={link.to}>
                            <Link
                              to={link.to}
                              className={`flex items-center px-4 py-2 rounded-md hover:bg-mauritania-green/10 ${
                                location.pathname === link.to
                                  ? "bg-mauritania-green/20 text-mauritania-green-dark font-semibold"
                                  : "text-gray-700"
                              }`}
                            >
                              <FontAwesomeIcon
                                icon={link.icon}
                                className="ml-2"
                              />
                              <span>
                                {language === "ar"
                                  ? link.labelAr
                                  : link.labelFr}
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default Sidebar;
